import { showRecommendationCards } from './recommendations';
import { updateHeaderLogo, closeDropdowns, clearSearch } from './headerFilter';
import initHowTo from './howTo';

/**
 * Extracts rotation angle from transform string
 * @param {string} transform - CSS transform string containing rotation
 * @returns {number} Rotation angle in degrees
 */
const getRotationAngle = (transform) => {
    return transform ? parseFloat(transform.split('rotate(')[1].split('deg')[0]) : 0;
};

/**
 * Calculates the difference between two angles for wheel rotation
 * @param {number} currentIndex - Index of the current segment
 * @param {number} prevIndex - Index of the previous segment
 * @returns {number} Angle difference in degrees
 */
const calculateAngleDifference = (currentIndex, prevIndex) => {
    const currentAngle = currentIndex * 30;
    const targetAngle = prevIndex * 30;
    let angleDiff = currentAngle - targetAngle;

    if (Math.abs(angleDiff) > 180) {
        angleDiff = angleDiff > 0 ? angleDiff - 360 : angleDiff + 360;
    }

    return angleDiff;
};

/**
 * Updates the active segment in the wheel and header
 * @param {HTMLElement} segment - The segment to be activated
 */
export const updateActiveSegment = (segment) => {
    const activeCategoryData = getActiveCategoryData(segment);
    const allSegments = document.querySelectorAll('.wheel__segment');
    allSegments.forEach((seg) => seg.classList.remove('active'));
    segment.classList.add('active');

    updateHeaderLogo(activeCategoryData.logo, activeCategoryData.title);
};

/**
 * Gets data for the active category
 * @param {HTMLElement} segment - The active segment element
 * @returns {Object} Object containing category id, logo, and title
 */
export const getActiveCategoryData = (segment) => {
    return {
        id: segment.getAttribute('id'),
        logo: segment.querySelector('.js-wheel--segment-image').src,
        title: segment.querySelector('.js-wheel--segment-title').textContent,
    };
};

/**
 * Rotates the wheel to the new position
 * @param {HTMLElement} wheelCircle - The wheel circle element
 * @param {HTMLElement} wheel - The main wheel element
 * @param {number} wheelRotation - Current wheel rotation
 * @param {number} angleDiff - Angle difference to rotate by
 */
const rotateWheel = (wheelCircle, wheel, wheelRotation, angleDiff) => {
    const newRotation = wheelRotation - angleDiff;
    wheel.classList.add('loading');
    wheelCircle.style.transform = `rotate(${newRotation}deg)`;
};

/**
 * Gets the current search query from the input field
 * @returns {string} Current search query
 */
const getSearchQuery = () => {
    const searchInput = document.querySelector('.js-news-search--input');
    return searchInput.value;
};

/**
 * Gets the ID of the currently active category
 * @returns {string} Active category ID
 */
const getActiveCategoryId = () => {
    const activeSegment = document.querySelector('.wheel__segment.active');
    return activeSegment.getAttribute('id');
};

/**
 * Gets the title of the currently active category
 * @returns {string} Active category title
 */
const getActiveCategoryTitle = () => {
    const activeSegment = document.querySelector('.wheel__segment.active');
    return activeSegment.getAttribute('data-category-title');
};

/**
 * Gets all active subcategories from the header filters
 * @returns {Object} Object containing taxonomy-term pairs
 */
const getSubcategories = () => {
    const items = document.querySelectorAll('.header__dropdown-item.active');
    return Array.from(items).reduce((acc, item) => {
        const taxonomyElement = item.closest('.js-header-filter--taxonomy');
        if (taxonomyElement) {
            const taxonomy = taxonomyElement.dataset.taxonomy;
            const term = item.dataset.term;

            if (term !== 'all') {
                acc[taxonomy] = term;
            }
        }
        return acc;
    }, {});
};

/**
 * Collects all necessary data for the search
 * @returns {Object} Object containing categoryId, searchQuery, and subcategories
 */
export const getData = (type = false) => {
    const categoryId = getActiveCategoryId();
    const searchQuery = getSearchQuery();
    const subcategories = getSubcategories();
    const categoryTitle = getActiveCategoryTitle();

    if (type === 'search') {
        return {
            categoryId: null,
            categoryTitle: null,
            searchQuery,
            subcategories,
        };
    }

    return {
        categoryId,
        searchQuery,
        subcategories,
        categoryTitle,
    };
};

/**
 * Prepares URL query parameters for the search
 * @returns {string} URL query string
 */
const prepareQueryParams = () => {
    const categoryId = getActiveCategoryId();
    const searchQuery = getSearchQuery();
    const subcategories = getSubcategories();

    const params = new URLSearchParams();
    params.set('category', categoryId);
    params.set('r-search', searchQuery);
    params.set('subcategories', encodeURIComponent(JSON.stringify(subcategories)));

    return params.toString();
};

/**
 * Shows recommendation cards based on current filters
 * @param {boolean} downWheel - Whether to animate the wheel down
 * @param {string} type - Type of results to show
 */
export const showResults = (downWheel = true, type = false) => {
    showRecommendationCards(getData(type), downWheel);
    closeDropdowns();
};

/**
 * Initializes the wheel component and sets up event listeners
 * Handles wheel rotation, segment selection, and results display
 */
export const initWheel = () => {
    /**
     * Wheel elements
     */
    const wheel = document.querySelector('.wheel');
    if (!wheel) return;

    const wheelCircle = wheel.querySelector('.wheel__circle');
    const segmentItems = document.querySelectorAll('.wheel__segment-content');

    /**
     * Remove not-ready class after 500ms
     */
    setTimeout(() => {
        wheel.classList.remove('not-ready');

        setTimeout(() => {
            initHowTo();
        }, 1000);
    }, 500);

    let lastClickTime = 0;
    /**
     * Handles wheel click
     * @param {HTMLElement} segment - The segment to be activated
     */
    const handleWheelClick = (segment, triggerShowResults = true) => {
        const currentTime = Date.now();
        if (currentTime - lastClickTime < 1000) return;
        lastClickTime = currentTime;

        let index = parseInt(segment.getAttribute('data-index'));
        const activeSegment = document.querySelector('.wheel__segment.active');
        let activeSegmentIndex = parseInt(activeSegment.getAttribute('data-index'));

        const wheelRotation = getRotationAngle(wheelCircle.style.transform);
        const angleDiff = calculateAngleDifference(index, activeSegmentIndex);

        rotateWheel(wheelCircle, wheel, wheelRotation, angleDiff);
        updateActiveSegment(segment);

        if (triggerShowResults) {
            showResults();
        }
    };

    /**
     * Dragging variables
     */
    let isDragging = false;
    let startX, startY;
    let startRotation;
    let isTouch = false;
    let dragTime = 0;

    /**
     * Gets the angle from mouse position
     * @param {number} centerX - The x coordinate of the center of the wheel
     * @param {number} centerY - The y coordinate of the center of the wheel
     * @param {number} mouseX - The x coordinate of the mouse
     * @param {number} mouseY - The y coordinate of the mouse
     * @returns {number} The angle in degrees
     */
    const getAngleFromMouse = (centerX, centerY, mouseX, mouseY) => {
        return Math.atan2(mouseY - centerY, mouseX - centerX) * (180 / Math.PI);
    };

    /**
     * Finds the closest segment to the current rotation
     * @param {number} rotation - The current rotation of the wheel
     * @returns {HTMLElement} The closest segment
     */
    const findClosestSegment = (rotation) => {
        const normalizedRotation = ((rotation % 360) + 360) % 360;
        let segmentIndex = Math.round(normalizedRotation / 30);
        segmentIndex = 13 - segmentIndex;
        segmentIndex = segmentIndex === 13 ? 0 : segmentIndex;

        return document.querySelector(`.wheel__segment[data-index="${segmentIndex}"]`);
    };

    /**
     * Handles the start of a drag
     * @param {number} clientX - The x coordinate of the mouse
     * @param {number} clientY - The y coordinate of the mouse
     */
    const handleDragStart = (clientX, clientY) => {
        if (wheel.classList.contains('loading')) return;

        isDragging = true;
        wheel.classList.add('dragging');
        wheelCircle.style.transition = 'none';
        dragTime = Date.now();

        startX = clientX;
        startY = clientY;
        startRotation = getRotationAngle(wheelCircle.style.transform);
    };

    /**
     * Handles the movement of the wheel
     * @param {number} clientX - The x coordinate of the mouse
     * @param {number} clientY - The y coordinate of the mouse
     */
    const handleDragMove = (clientX, clientY) => {
        if (!isDragging || wheel.classList.contains('loading')) return;

        const rect = wheelCircle.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        const startAngle = getAngleFromMouse(centerX, centerY, startX, startY);
        const currentAngle = getAngleFromMouse(centerX, centerY, clientX, clientY);

        const angleDiff = currentAngle - startAngle;
        const newRotation = startRotation + angleDiff;

        wheelCircle.style.transform = `rotate(${newRotation}deg)`;
    };

    /**
     * Handles the end of a drag
     */
    const handleDragEnd = () => {
        if (!isDragging) return;

        resetDragVariables();
        const currentRotation = getRotationAngle(wheelCircle.style.transform);
        const closestSegment = findClosestSegment(currentRotation);

        if (closestSegment) {
            const index = parseInt(closestSegment.getAttribute('data-index'));
            const activeSegment = document.querySelector('.wheel__segment.active');
            const activeSegmentIndex = parseInt(activeSegment.getAttribute('data-index'));

            const wheelRotation = currentRotation;
            addLoadingClass();

            updateDragRotation(wheelRotation, index, activeSegmentIndex);
            updateActiveSegment(closestSegment);
            showResults(false);
        }
    };

    /**
     * Adds the loading class to the wheel
     */
    const addLoadingClass = () => {
        wheel.classList.add('loading');
    };

    /**
     * Removes the loading class from the wheel
     */
    const removeLoadingClass = () => {
        wheel.classList.remove('loading');
    };

    /**
     * Gets the time of the drag
     * @returns {number} The time of the drag
     */
    const getDragTime = () => {
        return Date.now() - dragTime;
    };

    /**
     * Resets the drag variables
     */
    const resetDragVariables = () => {
        isDragging = false;
        wheel.classList.remove('dragging');
        wheelCircle.style.transition = '';
    };

    /**
     * Updates the drag rotation
     * @param {number} currentRotation - The current rotation of the wheel
     * @param {number} targetIndex - The index of the target segment
     */
    const updateDragRotation = (currentRotation, targetIndex) => {
        const targetAngle = targetIndex * 30;
        let angleDiff = targetAngle - 30;

        if (Math.abs(angleDiff) > 180) {
            angleDiff = angleDiff > 0 ? angleDiff - 360 : angleDiff + 360;
        }

        if (currentRotation < 0) {
            currentRotation = currentRotation * -1;
        }

        const appliedRotation = currentRotation - (currentRotation + angleDiff);
        wheelCircle.style.transform = `rotate(${appliedRotation}deg)`;
    };

    /**
     * Event listeners
     */
    wheelCircle.addEventListener('transitionend', () => {
        removeLoadingClass();
    });

    // segmentItems.forEach((item) => {
    //     item.addEventListener('click', (e) => {
    //         const segment = item.closest('.wheel__segment');
    //         handleWheelClick(segment);
    //     });
    // });

    const showResultsButtons = document.querySelectorAll('.js-wheel--show-results');
    showResultsButtons.forEach((button) => {
        button.addEventListener('click', function () {
            if (!this.classList.contains('js-wheel--show-results--main')) {
                clearSearch();
            }

            // Track Google Analytics event
            if (typeof gtag !== 'undefined') {
                const data = getData();
                gtag('event', 'view_results', {
                    event_category: 'wheel_interaction',
                    event_label: data.categoryTitle,
                    custom_parameters: {
                        category_id: data.categoryId,
                        search_query: data.searchQuery,
                        subcategories: JSON.stringify(data.subcategories),
                        segment_title: data.categoryTitle,
                    },
                });
            }

            showResults();
        });
    });

    wheelCircle.addEventListener('mousedown', (e) => {
        if (isTouch) return;
        if (wheel.classList.contains('loading') || isDragging) return;
        handleDragStart(e.clientX, e.clientY);

        e.preventDefault();
    });

    wheelCircle.addEventListener('touchstart', (e) => {
        isTouch = true;
        handleDragStart(e.touches[0].clientX, e.touches[0].clientY);
        e.preventDefault();
    });

    document.addEventListener('mousemove', (e) => {
        if (isTouch) return;
        handleDragMove(e.clientX, e.clientY);
    });

    document.addEventListener('touchmove', (e) => {
        handleDragMove(e.touches[0].clientX, e.touches[0].clientY);
        // e.preventDefault();
    });

    document.addEventListener('mouseup', (e) => {
        if (isTouch || !isDragging) return;

        const currentTime = Date.now();
        if (currentTime - lastClickTime < 300) {
            resetDragVariables();
            return;
        }

        if (getDragTime() < 200 && Math.abs(getRotationAngle(wheelCircle.style.transform) - startRotation) < 5) {
            resetDragVariables();
            const segment = e.target.closest('.wheel__segment');
            if (segment) {
                handleWheelClick(segment, false);
            }
        } else {
            handleDragEnd();
        }

        lastClickTime = currentTime;
    });

    document.addEventListener('touchend', (e) => {
        if (!isDragging) return;

        const currentTime = Date.now();
        if (currentTime - lastClickTime < 300) {
            resetDragVariables();
            return;
        }

        if (getDragTime() < 200 && Math.abs(getRotationAngle(wheelCircle.style.transform) - startRotation) < 5) {
            resetDragVariables();
            const segment = e.target.closest('.wheel__segment');
            if (segment) {
                handleWheelClick(segment, false);
            }
        } else {
            handleDragEnd();
        }

        lastClickTime = currentTime;

        setTimeout(() => {
            isTouch = false;
        }, 200);
    });
};

/**
 * Default export for the wheel component
 * Initializes the wheel component when the page loads
 */
export default initWheel;
